import FontAwesome from '@expo/vector-icons/FontAwesome';
import Ionicons from '@expo/vector-icons/Ionicons';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import { useCallback, useMemo, useState } from 'react';
import { View } from 'react-native';
import { Text } from '@/components/Text';
import { TabsContainer } from '@/components/TabsContainer';
import colors from '@/config/colors';
import { useWordsetContext } from '@/contexts/WordSetContext/useWordsetContext';
import { ContentText } from './ContentText';
import { Phonemauswahl } from './Phonemauswahl';
import { BASIC_CHECK_BOX, TABS_OPTIONS, WORTART } from './config';
import { styles } from './styles';
import { Modal } from '@/components/ui/therapist/Modal';
import { Button } from '@/components/ui/therapist/Button';
import { ScrollView } from 'react-native-gesture-handler';
import RadioButton from '@/components/ui/therapist/RadioButton';
import Checkbox from '@/components/ui/therapist/Checkbox';

export function Filter() {
  const {
    partsOfSpeech,
    changeOuterState,
    basic,
    changeBasicState,
    phonemes,
    refetchWords,
    toggleRefreshWordSetTrue,
    refreshWordsSet,
  } = useWordsetContext();

  const handlePartsOfSpeechChange = useCallback((value) => {
    return () => {
      changeOuterState('partsOfSpeech', value);
    };
  }, []);

  const handleBasicChange = (name, value, isRemove) => {
    changeBasicState(name, value, isRemove);
  };

  const CHECKBOXES =
    BASIC_CHECK_BOX[partsOfSpeech] ?? BASIC_CHECK_BOX['default'];

  const ACTIVE_CHECKBOXES = useMemo(() => {
    const component = CHECKBOXES.map((eachCheckBox) => {
      const CHECKBOX_ALL_VALUES = basic[eachCheckBox.name];
      return (
        <View style={styles.checkboxesContainer} key={eachCheckBox.name}>
          <Text style={[styles.checkboxItem, styles.checkboxTitle]}>
            {eachCheckBox.name}
          </Text>
          {eachCheckBox.values.map((value, index) => {
            if (value === null) {
              return <View key={index} style={styles.checkboxItem} />;
            }
            if (typeof value === 'object') {
              const isChecked = CHECKBOX_ALL_VALUES.includes(value.value);
              return (
                <View key={index} style={styles.checkboxItem}>
                  <Checkbox
                    label={value.display}
                    disabled={false}
                    isSelected={isChecked}
                    onPress={() =>
                      handleBasicChange(
                        eachCheckBox.name,
                        value.value,
                        isChecked,
                      )
                    }
                  />
                </View>
              );
            } else {
              const isChecked = CHECKBOX_ALL_VALUES.includes(value);
              return (
                <View key={index} style={styles.checkboxItem}>
                  <Checkbox
                    label={value === 5 ? '5+' : value}
                    disabled={false}
                    isSelected={isChecked}
                    onPress={() =>
                      handleBasicChange(eachCheckBox.name, value, isChecked)
                    }
                  />
                </View>
              );
            }
          })}
        </View>
      );
    });
    return component;
  }, [CHECKBOXES.length, basic, refreshWordsSet]);

  const WORTART_RADIO = useMemo(() => {
    return WORTART.map((eachWortart) => {
      const { id, name, value } = eachWortart ?? {};
      const isSelected = value === partsOfSpeech;
      return (
        <View key={id} style={styles.filterRadioItem}>
          <RadioButton
            isSelected={isSelected}
            label={name}
            onPress={handlePartsOfSpeechChange(value)}
            labelStyle={{ fontWeight: 'bold' }}
          />
        </View>
      );
    });
  }, [partsOfSpeech]);

  const [showModal, setShowModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);

  const toggleModal = useCallback(() => {
    setShowModal((prevState) => !prevState);
  }, []);

  const toggleFilterModal = useCallback(() => {
    setShowFilterModal((prevState) => {
      if (prevState) {
        refetchWords();
      }
      return !prevState;
    });
  }, [refetchWords]);

  const hasLightGreyBg = useCallback((phonemes) => {
    const { initialUnselected, medialUnselected, finalUnselected } = phonemes;
    if (
      initialUnselected.length === 0 &&
      medialUnselected.length === 0 &&
      finalUnselected.length === 0
    ) {
      return true;
    } else {
      return false;
    }
  }, []);

  const isLightGreyBg = hasLightGreyBg(phonemes);

  return (
    <>
      <View style={[styles.filter]}>
        <View style={styles.filterStatic}>
          <View style={styles.filterRadioContainer}>
            <Text
              style={[styles.filterRadioContainerTitle, styles.filterRadioItem]}
            >
              Wortart
            </Text>
            {WORTART_RADIO}
          </View>
          <View style={styles.filterButtons}>
            <PressableNeolexon
              style={[
                styles.filterButton,
                styles.buttonShadowDrop,
                styles.marginRight,
              ]}
              onPress={toggleRefreshWordSetTrue}
            >
              <Ionicons name="refresh-sharp" size={24} color="black" />
            </PressableNeolexon>
            <PressableNeolexon
              onPress={toggleModal}
              style={[styles.filterButton, styles.buttonShadowDrop]}
            >
              <FontAwesome name="question" size={24} color="black" />
            </PressableNeolexon>
          </View>
        </View>
        <View>{ACTIVE_CHECKBOXES}</View>
        <PressableNeolexon
          onPress={toggleFilterModal}
          style={[
            styles.phonemauswahlBtn,
            styles.buttonShadowDrop,
            isLightGreyBg ? styles.hasLightGrayBg : styles.hasLightGreenBg,
          ]}
        >
          <Text
            style={{ color: isLightGreyBg ? colors.darkGrey : colors.white }}
          >
            Phonemauswahl
          </Text>
        </PressableNeolexon>
      </View>
      <Modal
        onClose={() => setShowFilterModal(false)}
        isVisible={showFilterModal}
        confirmActionButton={
          <Button label="Speichern und schließen" onPress={toggleFilterModal} />
        }
        title="Phonemauswahl"
        customStyle={{ minHeight: '80%', overflow: 'scroll' }}
      >
        <TabsContainer options={TABS_OPTIONS} defaultTab={TABS_OPTIONS[0]}>
          <Phonemauswahl />
        </TabsContainer>
      </Modal>
      <Modal
        onClose={toggleModal}
        isVisible={showModal}
        confirmActionButton={<Button label="Schließen" onPress={toggleModal} />}
        customStyle={{ minHeight: '70%' }}
        title="Hier legen Sie fest, welche Eigenschaften die Wörter haben, die Ihnen angezeigt werden."
      >
        <ScrollView>
          <ContentText />
        </ScrollView>
      </Modal>
    </>
  );
}
